import { DeepPartial } from 'utility-types'
import { z } from 'zod'

import { AVAILABLE_CURRENCIES } from 'common/settings/interfaces'
import { FilterSchema } from 'shared/api/common/schemas/filters'
import {
  channelData,
  WaMMLiteStatus,
  WaCoexistenceImportContactsPeriod,
  WaMessagingLimitTier,
} from 'shared/api/common/schemas/whatsAppChannelData'

import { MMLiteUpToDateErrorSchema } from './errors'

export enum RequestPhoneStatus {
  SENT_CODE = 'sent_code',
  CONNECTED = 'connected',
  ERROR = 'error',
}
export enum VerifyPhoneStatus {
  WRONG_CODE = 'wrong_code',
  CONNECTED = 'connected',
  ERROR = 'error',
}

export type RequestCodeResponse = z.infer<typeof whatsAppApiSchemas.requestCode.response>
export type VerifyCodeStatusResponse = z.infer<typeof whatsAppApiSchemas.verifyCode.response>

export enum WaMessageTemplateStatus {
  PENDING = 'PENDING',
  REJECTED = 'REJECTED',
  APPROVED = 'APPROVED',
  DRAFT = 'DRAFT',
  DELETED = 'DELETED',
  PAUSED = 'PAUSED',
  DISABLED = 'DISABLED',
}

export enum WaMessageTemplateHeaderType {
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
  TEXT = 'TEXT',
  DOCUMENT = 'DOCUMENT',
}

export enum WaMessageTemplateCategoryId {
  ACCOUNT_UPDATE = 'ACCOUNT_UPDATE',
  PAYMENT_UPDATE = 'PAYMENT_UPDATE',
  PERSONAL_FINANCE_UPDATE = 'PERSONAL_FINANCE_UPDATE',
  SHIPPING_UPDATE = 'SHIPPING_UPDATE',
  RESERVATION_UPDATE = 'RESERVATION_UPDATE',
  ISSUE_RESOLUTION = 'ISSUE_RESOLUTION',
  APPOINTMENT_UPDATE = 'APPOINTMENT_UPDATE',
  TRANSPORTATION_UPDATE = 'TRANSPORTATION_UPDATE',
  TICKET_UPDATE = 'TICKET_UPDATE',
  ALERT_UPDATE = 'ALERT_UPDATE',
  AUTO_REPLY = 'AUTO_REPLY',
  MARKETING_LITE = 'MARKETING_LITE',
  // WhatsApp API V16
  AUTHENTICATION = 'AUTHENTICATION',
  MARKETING = 'MARKETING',
  UTILITY = 'UTILITY',
}

export enum WaMessageTemplateButtonType {
  QuickReply = 'QUICK_REPLY',
  Url = 'URL',
}

export enum WaMessageTemplateRejectionReason {
  ABUSIVE_CONTENT = 'ABUSIVE_CONTENT',
  INCORRECT_CATEGORY = 'INCORRECT_CATEGORY',
  INVALID_FORMAT = 'INVALID_FORMAT',
  PROMOTIONAL = 'PROMOTIONAL',
  SCAM = 'SCAM',
  TAG_CONTENT_MISMATCH = 'TAG_CONTENT_MISMATCH',
  NONE = 'NONE',
}

export enum WaMessageTemplatePauseTitle {
  PAUSE_FOR_3_HOURS = 'first',
  PAUSE_FOR_6_HOURS = 'second',
}

export enum CreditLineStatus {
  SHARED = 'SHARED',
  NOT_SHARED = 'NOT_SHARED',
  PRO_REQUIRED = 'PRO_REQUIRED',
  CANNOT_BE_SHARED = 'CANNOT_BE_SHARED',
}

export enum WaBroadcastFilterOperator {
  AND = 'AND',
  IS = 'IS',
  OR = 'OR',
}

const messageTemplateCategory = z.object({
  id: z.nativeEnum(WaMessageTemplateCategoryId),
  title: z.string(),
  description: z.string().nullish(),
})

const messageTemplateLanguage = z.object({
  label: z.string(),
  value: z.string(),
})

const messageTemplateLanguagesList = z.array(messageTemplateLanguage)

const messageTemplateButton = z.object({
  type: z.nativeEnum(WaMessageTemplateButtonType),
  text: z.string(),
  url: z.string().optional(),
  variable: z.string().optional(),
})

const baseMessageTemplateHeaderAttachment = z.object({
  url: z.string(),
  format: z.string().optional(),
  name: z.string().optional(),
  // in backend data base type is sometimes string and sometimes number
  // even if its not convienient, we have to handle both cases
  caid: z.number().optional().or(z.string().optional()),
})

const singleMessageTemplateHeader = z.discriminatedUnion('type', [
  z.object({
    type: z.literal(WaMessageTemplateHeaderType.TEXT),
    text: z.string(),
  }),
  baseMessageTemplateHeaderAttachment.extend({
    type: z.literal(WaMessageTemplateHeaderType.DOCUMENT),
  }),
  baseMessageTemplateHeaderAttachment.extend({
    type: z.literal(WaMessageTemplateHeaderType.IMAGE),
  }),
  baseMessageTemplateHeaderAttachment.extend({
    type: z.literal(WaMessageTemplateHeaderType.VIDEO),
  }),
])

const messageTemplateHeader = z.union([
  singleMessageTemplateHeader,
  z.array(singleMessageTemplateHeader),
])

export const messageTemplate = z.object({
  account_id: z.number(),
  template_id: z.number(),
  language_code: z.string(),
  name: z.string(),
  name_human: z.string(),
  status: z.nativeEnum(WaMessageTemplateStatus),
  category: z.nativeEnum(WaMessageTemplateCategoryId),
  header: messageTemplateHeader,
  body: z.string(),
  footer: z.string().optional(),
  buttons: z.array(messageTemplateButton),
  ts_created: z.number(),
  ts_updated: z.number(),
  ts_deleted: z.number().nullable(),
  is_default: z.boolean(),
  rejected_reason: z.nativeEnum(WaMessageTemplateRejectionReason).nullable(),
  pause_title: z.nativeEnum(WaMessageTemplatePauseTitle).optional().nullable(),
  pause_time: z.number().optional().nullable(),
  unpause_time: z.number().optional().nullable(),
})

const messageTemplateDraft = messageTemplate.partial().extend({
  example: z.record(z.string(), z.string()).optional(),
})

const messageTemplatePack = z.array(messageTemplate)

const healthWidgetData = channelData.pick({
  business_verification_status: true,
  messaging_limit_tier: true,
  account_review_status: true,
  meta_phone_status: true,
})

const whatsAppBusinessProfileSchema = z.object({
  field: z.string(),
  value: z.union([z.string(), z.array(z.string())]),
})

export const whatsAppApiSchemas = {
  refreshMMLiteStatus: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: z.object({
      mm_lite_status: z.nativeEnum(WaMMLiteStatus),
    }),
    errors: {
      upToDate: MMLiteUpToDateErrorSchema,
    },
  },
  verifyCode: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      code: z.string(),
    }),
    response: z.object({
      result: z.nativeEnum(VerifyPhoneStatus),
      error: z.string().optional(),
    }),
  },
  disconnectChannel: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: z.undefined(),
  },
  requestCode: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      method: z.string(),
    }),
    response: z.object({
      error: z.string().optional(),
      result: z.nativeEnum(RequestPhoneStatus),
    }),
  },
  coexistenceImportContacts: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      coexistence_import_contacts_period: z.nativeEnum(WaCoexistenceImportContactsPeriod),
    }),
    response: z.undefined(),
  },
  fetchMessageTemplateCategories: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: z.object({
      categories: z.array(messageTemplateCategory),
    }),
  },
  listMessageTemplateLanguages: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: z.object({
      values: messageTemplateLanguagesList,
    }),
  },
  getMessageTemplatePack: {
    path: z.undefined(),
    query: z.object({
      name: z.string(),
    }),
    request: z.undefined(),
    response: z.object({
      message_template: messageTemplatePack,
    }),
  },
  getBicLimits: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: z.object({
      conversation_count: z.number(),
      messaging_limit_tier: z.nativeEnum(WaMessagingLimitTier).nullable(),
    }),
  },
  estimateBroadcastCost: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      filter: FilterSchema,
      category: z.nativeEnum(WaMessageTemplateCategoryId),
    }),
    response: z.object({
      cost: z.number().nonnegative(),
      currency: z.nativeEnum(AVAILABLE_CURRENCIES),
      currency_symbol: z.string(),
      us_numbers_count: z.number().optional(),
    }),
  },
  listMessageTemplatesPacks: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: z.object({
      message_templates: z.record(z.string(), messageTemplatePack).or(z.tuple([])),
    }),
  },
  createMessageTemplate: {
    path: z.undefined(),
    query: z.undefined(),
    request: messageTemplateDraft,
    response: z.object({
      message_template: messageTemplate,
    }),
  },
  editMessageTemplate: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      id: messageTemplate.shape.template_id,
      header: messageTemplate.shape.header.optional(),
      body: messageTemplate.shape.body.optional(),
      footer: messageTemplate.shape.footer.optional(),
      example: messageTemplateDraft.shape.example.optional(),
      buttons: messageTemplate.shape.buttons.optional(),
      category: messageTemplate.shape.category.optional(),
    }),
    response: z.object({
      message_template: messageTemplate,
    }),
  },
  deleteMessageTemplates: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      names: z.array(z.string()),
    }),
    response: z.object({
      templates_deleted: z.array(z.string()),
    }),
  },
  updateDefaultMessageTemplate: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      name: z.string(),
      language: z.string(),
    }),
    response: z.undefined(),
  },
  updateMessageTemplateVariables: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      id: z.number(),
      variables: z.record(z.string(), z.record(z.string(), z.string())),
    }),
    response: z.object({
      message_template: messageTemplate,
    }),
  },
  getChannelData: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: z.object({
      whatsapp_channel: channelData,
    }),
  },
  refreshHealthWidgetData: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: healthWidgetData,
  },
  needsPermissionRefresh: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: z.object({
      is_need_refresh: z.boolean(),
    }),
  },
  clearNewBusinessAccountNameData: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: z.undefined(),
  },
  confirmNewBusinessAccountName: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: z.undefined(),
  },
  setCatalogVisibility: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      value: z.boolean(),
    }),
    response: z.undefined(),
  },
  getWhatsAppCommerceSettings: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: z.object({
      is_cart_enabled: z.boolean(),
      is_catalog_visible: z.boolean(),
    }),
  },
  fixBrokenCarts: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: z.undefined(),
  },
  getAdAccountsList: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: z.object({
      list: z.array(
        z.object({
          id: z.string(),
          name: z.string(),
        }),
      ),
    }),
  },
  getAdsList: {
    path: z.undefined(),
    query: z.object({ ad_account_id: z.string() }),
    request: z.undefined(),
    response: z.object({
      list: z.array(
        z.object({
          id: z.string(),
          name: z.string(),
          flow_ns: z.string().nullish(),
        }),
      ),
    }),
  },
  getStats: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: z.object({
      stats: z.object({
        total_count: z.number(),
        total_cost: z.number(),
        free_period_end: z.string().nullable(),
      }),
    }),
  },
  setWhatsAppBusinessProfile: {
    path: z.undefined(),
    query: z.undefined(),
    request: whatsAppBusinessProfileSchema,
    response: z.object({
      whatsapp_channel: channelData,
    }),
  },
  setWhatsAppProfilePhoto: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.instanceof(FormData),
    response: z.object({
      whatsapp_channel: channelData,
    }),
  },
}

export type WaGetAdAccountsListResponse = z.infer<
  typeof whatsAppApiSchemas.getAdAccountsList.response
>
export type WaGetAdsListResponse = z.infer<typeof whatsAppApiSchemas.getAdsList.response>

export type WaDeleteMessageTemplatesRequest = z.infer<
  typeof whatsAppApiSchemas.deleteMessageTemplates.request
>
export type WaUpdateDefaultMessageTemplateRequest = z.infer<
  typeof whatsAppApiSchemas.updateDefaultMessageTemplate.request
>
export type WaEditMessageTemplateRequest = DeepPartial<
  z.infer<typeof whatsAppApiSchemas.editMessageTemplate.request>
>
export type WaEditMessageTemplateRequestFull = z.infer<
  typeof whatsAppApiSchemas.editMessageTemplate.request
>
export type WaUpdateMessageTemplateVariablesRequest = z.infer<
  typeof whatsAppApiSchemas.updateMessageTemplateVariables.request
>

export type WaMessageTemplateSingleHeader = z.infer<typeof singleMessageTemplateHeader>
export type WaMessageTemplate = z.infer<typeof messageTemplate>
export type WaMessageTemplateDraft = DeepPartial<z.infer<typeof messageTemplateDraft>>
export type WaMessageTemplateButton = z.infer<typeof messageTemplateButton>
export type WaMessageTemplatePack = z.infer<typeof messageTemplatePack>
export type WaMessageTemplatesList = Record<string, WaMessageTemplatePack>
export type WaMessageTemplateCategory = z.infer<typeof messageTemplateCategory>
export type WaMessageTemplateCategories = WaMessageTemplateCategory[]
export type WaMessageTemplateLanguage = z.infer<typeof messageTemplateLanguage>
export type WaMessageTemplateHeader = z.infer<typeof messageTemplateHeader>
export type WaMessageTemplateHeaderTypeAttachment =
  | Extract<
      WaMessageTemplateHeader,
      {
        type: WaMessageTemplateHeaderType.VIDEO
      }
    >
  | Extract<
      WaMessageTemplateHeader,
      {
        type: WaMessageTemplateHeaderType.DOCUMENT
      }
    >
  | Extract<
      WaMessageTemplateHeader,
      {
        type: WaMessageTemplateHeaderType.IMAGE
      }
    >

export type WhatsAppHealthWidgetData = z.infer<typeof healthWidgetData>

export type WhatsAppBroadcastingBicLimits = z.infer<typeof whatsAppApiSchemas.getBicLimits.response>
export type WhatsAppBroadcastingCostEstimation = z.infer<
  typeof whatsAppApiSchemas.estimateBroadcastCost.response
>
export type WhatsAppCommerceSettings = z.infer<
  typeof whatsAppApiSchemas.getWhatsAppCommerceSettings.response
>

export type WhatsAppCatalogNeedsPermissionRefresh = z.infer<
  typeof whatsAppApiSchemas.needsPermissionRefresh.response
>

export type WhatsAppFixBrokenCarts = z.infer<typeof whatsAppApiSchemas.fixBrokenCarts.response>

export type WaBusinessProfileRequest = z.infer<typeof whatsAppBusinessProfileSchema>
export type WaBusinessProfileResponse = z.infer<
  typeof whatsAppApiSchemas.setWhatsAppBusinessProfile.response
>
export type WaProfilePhotoResponse = z.infer<
  typeof whatsAppApiSchemas.setWhatsAppProfilePhoto.response
>
